import React, { useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import { MatureDeviationContext } from '../../Context/MatureDeviationContext';
import { normalizePremiumFolderData } from '../../utils/normalizePremiumFolderData';
import { useSubscriptionTier } from '../../utils/hooks/useSubscriptionTier';
import { overrideSubscribeMatureFilter } from '../../Tier/redux/consumer/actions';
import { DeviationTypes } from '../../types/deviation';
import { fetchMedia } from '../../DeviationPage/redux/actions';
import { getDeviationType, getShouldDisplayPaywall } from '../_helpers';
import HiddenOverlay from '../HiddenOverlay';
import FolderPaywallFullView from './_partials/FolderPaywall';
import SubPaywall from './_partials/SubPaywall';
import ImageFullView from './Image';
import FilmFullView from './Film';
import JournalFullView from './Journal';
import LiteratureFullView from './Literature';
import PdfFullView from './Pdf';
import PollFullView from './Poll';
import StatusFullView from './Status';
import CommissionFullView from './Commission';
import TierFullView from './Tier';
import UnknownFullView from './Unknown';
import { FullViewProps } from './types';

const typesWithBlurredPreviewBehindBlock = {
  [DeviationTypes.IMAGE]: ImageFullView,
  [DeviationTypes.ORIGINALS]: ImageFullView,
  [DeviationTypes.FILM]: FilmFullView,
  [DeviationTypes.PDF]: PdfFullView,
};

const DeviationFullView: React.FC<FullViewProps> = props => {
  const { deviation } = props;
  const deviationType = getDeviationType(deviation);

  const { isSubscribedButBlocked, needsSubscription } = useSubscriptionTier(deviation);

  // if the user's settings say to, display a blocking overlay
  // on deviations that are mature...
  const { showBlocked, overrideMatureFilter } = useContext(MatureDeviationContext);
  const consistent_devpage_blur = useFlag('consistent_devpage_blur');
  const dispatch = useDispatch();
  const overrideMatureFilterWrapper = useCallback(() => {
    if (deviationType === DeviationTypes.TIER) {
      dispatch(overrideSubscribeMatureFilter(deviation));
    }
    // (blurred mature deviations need to have their media refreshed
    // w/ non-blurred versions if/when removing the hidden overlay)
    dispatch(fetchMedia(deviation));
    overrideMatureFilter();
  }, [overrideMatureFilter, dispatch, deviation, deviationType]);

  if (showBlocked) {
    const FullView = typesWithBlurredPreviewBehindBlock[deviationType];
    const showBlurredPreviewBehindBlock = consistent_devpage_blur && !!FullView;
    return showBlurredPreviewBehindBlock ? (
      <>
        <HiddenOverlay
          overrideMatureFilter={overrideMatureFilterWrapper}
          {...props}
          showBackground={false}
          useBackendBlur
        />
        <FullView {...props} />
      </>
    ) : (
      <HiddenOverlay overrideMatureFilter={overrideMatureFilterWrapper} {...props} />
    );
  }

  const { isPremiumLocked } = normalizePremiumFolderData(deviation?.premiumFolderData);
  if (isPremiumLocked && deviationType !== DeviationTypes.LITERATURE) {
    return <FolderPaywallFullView {...props} />;
  }

  const isBlocked = needsSubscription || isSubscribedButBlocked;
  if (isBlocked && getShouldDisplayPaywall(deviationType)) {
    return <SubPaywall {...props} showImage />;
  }

  switch (deviationType) {
    case DeviationTypes.IMAGE:
    case DeviationTypes.ORIGINALS:
      return <ImageFullView {...props} />;
    case DeviationTypes.FILM:
      return <FilmFullView {...props} />;
    case DeviationTypes.PDF:
      return <PdfFullView {...props} />;
    case DeviationTypes.LITERATURE:
      return <LiteratureFullView {...props} />;
    case DeviationTypes.JOURNAL:
      return <JournalFullView {...props} />;
    case DeviationTypes.STATUS:
      return <StatusFullView {...props} />;
    case DeviationTypes.POLL:
      return <PollFullView {...props} />;
    case DeviationTypes.COMMISSION:
      return <CommissionFullView {...props} />;
    case DeviationTypes.TIER:
      return <TierFullView {...props} />;
    default:
      return <UnknownFullView {...props} type={deviationType} />;
  }
};

export default DeviationFullView;
